// RangeFacet — covers date / money / number ranges with a min/max input pair.
//
// We deliberately use plain native inputs (no slider) — operators paste
// numbers and dates from spreadsheets. A slider would lose them precision.
//
// Optional `relativePresets` (date kind only) renders a row of pills above
// the inputs. Selecting a preset serializes to absolute ISO timestamps
// (now − offset → now) at submit time so the URL stays deterministic.

export type RangeKind = 'date' | 'money' | 'number';

export interface RangeFacetValue {
  from?: string | null; // date kind
  to?: string | null;
  min?: number | null; // money/number kind
  max?: number | null;
  currency?: string;
}

export interface RangeRelativePreset {
  label: string; // "1h" | "24h" | "7d" | "30d"
  fromOffsetMs: number; // distance back from "now"
}

export interface RangeFacetProps {
  field: string;
  label: string;
  kind: RangeKind;
  value: RangeFacetValue;
  onChange: (next: RangeFacetValue) => void;
  step?: number;
  /** Date-kind only. Quick relative-time pills rendered above the inputs. */
  relativePresets?: RangeRelativePreset[];
}

export function RangeFacet({
  field,
  label,
  kind,
  value,
  onChange,
  step,
  relativePresets,
}: RangeFacetProps) {
  const isDate = kind === 'date';
  const aLabel = isDate ? 'from' : 'min';
  const bLabel = isDate ? 'to' : 'max';
  const aValue: string = isDate ? (value.from ?? '') : value.min == null ? '' : String(value.min);
  const bValue: string = isDate ? (value.to ?? '') : value.max == null ? '' : String(value.max);

  const setA = (v: string) => {
    if (isDate) onChange({ ...value, from: v || null });
    else onChange({ ...value, min: v === '' ? null : Number(v) });
  };
  const setB = (v: string) => {
    if (isDate) onChange({ ...value, to: v || null });
    else onChange({ ...value, max: v === '' ? null : Number(v) });
  };
  const applyPreset = (offsetMs: number) => {
    const now = Date.now();
    onChange({
      ...value,
      from: new Date(now - offsetMs).toISOString(),
      to: new Date(now).toISOString(),
    });
  };

  const dirty = isDate ? Boolean(value.from || value.to) : value.min != null || value.max != null;

  return (
    <fieldset
      data-facet={field}
      style={{
        border: 'none',
        borderTop: '1px solid var(--bd)',
        margin: 0,
        padding: '8px 10px 10px',
      }}
    >
      <legend
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 0,
          color: 'var(--fg-3)',
          fontFamily: 'var(--mono)',
          fontSize: 10,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        <span>{label}</span>
        {dirty && (
          <button
            type="button"
            onClick={() => onChange(isDate ? { from: null, to: null } : { min: null, max: null })}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--fg-3)',
              fontFamily: 'var(--mono)',
              fontSize: 10,
            }}
          >
            CLR
          </button>
        )}
      </legend>
      {isDate && relativePresets && relativePresets.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {relativePresets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => applyPreset(preset.fromOffsetMs)}
              style={{
                height: 18,
                padding: '0 6px',
                background: 'var(--bg-pane-2)',
                border: '1px solid var(--bd-strong)',
                color: 'var(--fg-2)',
                fontFamily: 'var(--mono)',
                fontSize: 10,
                cursor: 'pointer',
                lineHeight: 1,
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}
      <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <RangeInput label={aLabel} kind={kind} value={aValue} step={step} onChange={setA} />
        <RangeInput label={bLabel} kind={kind} value={bValue} step={step} onChange={setB} />
      </div>
    </fieldset>
  );
}

function RangeInput({
  label,
  kind,
  value,
  step,
  onChange,
}: {
  label: string;
  kind: RangeKind;
  value: string;
  step?: number;
  onChange: (v: string) => void;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span
        style={{ color: 'var(--fg-3)', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 0.4 }}
      >
        {label.toUpperCase()}
      </span>
      <input
        type={kind === 'date' ? 'date' : 'number'}
        value={value}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          height: 24,
          padding: '0 6px',
          background: 'var(--bg-base)',
          border: '1px solid var(--bd-strong)',
          color: 'var(--fg-1)',
          fontFamily: 'var(--mono)',
          fontSize: 11,
        }}
      />
    </label>
  );
}
